import {
  allocateStrokes,
  courseHandicap,
  effectiveAllowancePercent,
  holesInPlay,
  playingHandicap,
} from "../handicap.js";
import type { Player, Round, Score, TeeSet } from "../types.js";
import { splitTeamPoints } from "../ties.js";
import type { Contest, Format, RoundResult } from "./types.js";
import {
  allowanceNote,
  buildScoreMap,
  grossOnHole,
  holeCount,
  holeNumbersInPlay,
  holesInSegment,
  pairSidesIntoMatches,
} from "./util.js";

type Sides = "singles" | "fourBall";

interface MatchPlayConfig {
  sides?: Sides;
}

/** What one hole did to one match. */
type MatchHoleState =
  /** Both sides posted, so the hole has a result — `winner` says whose (null = halved). */
  | "played"
  /** A side has no score here. Nothing is decided by it. */
  | "gap"
  /** The match was already mathematically over by the time this hole came round. */
  | "over";

interface MatchHole {
  hole: number;
  state: MatchHoleState;
  /** Net each side played it in, in side order. Null where a side didn't post. */
  values: [number | null, number | null];
  /** Which side took it, or null for a halve (and for every non-"played" hole). */
  winner: 0 | 1 | null;
  /** Holes won by each side once this hole is in. */
  won: [number, number];
}

interface MatchWalk {
  segmentIndex: number;
  startHole: number;
  endHole: number;
  sides: [string[], string[]];
  holes: MatchHole[];
  won: [number, number];
  /**
   * Whether the match reached a result. A lead no bigger than the holes left is a match
   * still in progress, and pays nobody — see the walk for why a gap can mean either.
   */
  decided: boolean;
  /** Holes never played because the match was already won — the "2" in "3&2". */
  remaining: number;
}

/**
 * The single walk through a match play round.
 *
 * `score` used to run this inline and return only the segment points, which meant the one
 * format whose entire result is "who won which hole" was the one format that couldn't
 * say. Extracting it follows Skins (`walkPot`), and for the same reason: two replays of
 * the same holes drift apart, and here they would disagree about who won the match.
 *
 * Every hole in the segment comes back, including the ones after a clinch — a match that
 * ends 5&4 really did leave four holes unplayed, and saying so is the point.
 */
function walkMatches(
  round: Round,
  course: TeeSet,
  players: Player[],
  scores: Score[],
  onlyHoles: number[] | undefined,
  settled: boolean | undefined,
): MatchWalk[] {
  const scoreMap = buildScoreMap(scores);
  const holeNumbers = holeNumbersInPlay(round, course);
  const allocationHoles = holesInPlay(course.holes, round.holeSet);
  const allowance = effectiveAllowancePercent(round.handicapAllowance, allocationHoles.length);
  const playerById = new Map(players.map((p) => [p.id, p]));

  /** Strokes each player in one match receives, off the lowest handicap in that match. */
  const strokesForMatch = (ids: string[]): Map<string, Map<number, number>> => {
    const playing = ids.map((id) => {
      const player = playerById.get(id);
      if (!player) return 0;
      return playingHandicap(courseHandicap(player.handicapIndex, course.slope), allowance);
    });
    const lowest = playing.length > 0 ? Math.min(...playing) : 0;
    return new Map(
      ids.map((id, i) => [id, allocateStrokes((playing[i] ?? 0) - lowest, allocationHoles)]),
    );
  };

  const walks: MatchWalk[] = [];

  for (const [segmentIndex, segment] of round.segments.entries()) {
    const allHoles = holesInSegment(holeNumbers, segment.startHole, segment.endHole);
    const played = onlyHoles ? new Set(onlyHoles) : null;
    // Once the organiser has closed the round, the holes that were played are the
    // match — that is how an abandoned round still pays out. While it is open, an
    // unplayed hole stays in the list as a gap, which is what stops an undecided
    // match from being settled as a win.
    const matchHoles = settled && played ? allHoles.filter((h) => played.has(h)) : allHoles;

    const sides = segment.teams.filter((t) => t.length > 0);

    for (const [a, b] of pairSidesIntoMatches(sides)) {
      const strokes = strokesForMatch([...a, ...b]);
      const netOn = (side: string[], hole: number): number | null => {
        if (played && !played.has(hole)) return null;
        let best: number | null = null;
        for (const id of side) {
          const gross = grossOnHole(scoreMap, id, hole);
          if (gross == null) continue;
          const net = gross - (strokes.get(id)?.get(hole) ?? 0);
          if (best == null || net < best) best = net;
        }
        return best;
      };

      const won: [number, number] = [0, 0];
      let decided = matchHoles.length > 0;
      const holes: MatchHole[] = [];
      let stoppedAt = matchHoles.length;

      for (let i = 0; i < matchHoles.length; i++) {
        const hole = matchHoles[i]!;
        const values: [number | null, number | null] = [netOn(a, hole), netOn(b, hole)];
        if (values.some((v) => v == null)) {
          // Real match play stops once a side is mathematically unbeatable (e.g.
          // "5&4") — the trailing holes are never played, so a gap there is the
          // clinch rather than missing data. A lead no bigger than the holes left is
          // a match still in progress, and pays nobody.
          decided = Math.abs(won[0] - won[1]) > matchHoles.length - i;
          stoppedAt = i;
          break;
        }
        const [av, bv] = values as [number, number];
        let winner: 0 | 1 | null = null;
        if (av < bv) {
          won[0]++;
          winner = 0;
        } else if (bv < av) {
          won[1]++;
          winner = 1;
        }
        // equal nets halve the hole, and nobody banks it

        holes.push({ hole, state: "played", values, winner, won: [won[0], won[1]] });

        if (Math.abs(won[0] - won[1]) > matchHoles.length - (i + 1)) {
          stoppedAt = i + 1;
          break;
        }
      }

      // Everything the walk stopped short of. A decided match left them unplayed because
      // it was already over; an undecided one is simply still missing scores.
      const trailingState: MatchHoleState = decided ? "over" : "gap";
      for (let i = stoppedAt; i < matchHoles.length; i++) {
        holes.push({
          hole: matchHoles[i]!,
          state: trailingState,
          values: [null, null],
          winner: null,
          won: [won[0], won[1]],
        });
      }

      walks.push({
        segmentIndex,
        startHole: segment.startHole,
        endHole: segment.endHole,
        sides: [a, b],
        holes,
        won: [won[0], won[1]],
        decided,
        remaining: matchHoles.length - stoppedAt,
      });
    }
  }

  return walks;
}

/** The match state after a hole, read from `side`'s point of view. */
function matchStatus(won: [number, number], side: 0 | 1): string {
  const diff = side === 0 ? won[0] - won[1] : won[1] - won[0];
  if (diff === 0) return "AS";
  return diff > 0 ? `${diff} up` : `${-diff} dn`;
}

/** Where a match stands, in the way match play actually says it: "3&2", "2 up", "AS". */
function describeStanding(walk: MatchWalk, labels: [string, string]): string {
  const holesPlayed = walk.holes.filter((h) => h.state === "played").length;
  if (holesPlayed === 0) return "Not started";

  const diff = walk.won[0] - walk.won[1];
  const leader = diff > 0 ? labels[0] : labels[1];

  if (!walk.decided) {
    return diff === 0
      ? `All square through ${holesPlayed}`
      : `${leader} ${Math.abs(diff)} up through ${holesPlayed}`;
  }
  if (diff === 0) return "Match halved";
  // "3&2" is 3 up with 2 left to play; a match that goes the distance is just "2 up".
  return walk.remaining > 0
    ? `${leader} wins ${Math.abs(diff)}&${walk.remaining}`
    : `${leader} wins ${Math.abs(diff)} up`;
}

/**
 * Hole-by-hole win/half/loss. A segment's side can be a single player (singles) or a
 * pair (four-ball), and sides pair off within the segment — side 1 plays side 2, side 3
 * plays side 4 — so a six-player field runs three matches over the same holes rather
 * than one three-way race.
 *
 * Match play does not share the engine's handicap machinery, and that is the point.
 * Every other format compares players against the course, so each having their own full
 * playing handicap is right. Match play compares two sides against *each other*, and the
 * Rules allocate strokes on the difference: the lowest playing handicap in the match
 * plays off scratch and everyone else is reduced by that amount. Allocating in full
 * instead moves which holes carry a stroke — with course handicaps 11 and 6 the Rules
 * give five strokes on SI 1-5, while full allocation nets out to an advantage on SI 7-11
 * — and different holes won means a different match result.
 *
 * The reduction is per player, not per side (the WHS four-ball rule), and per match, not
 * per segment, because concurrent matches are independent contests.
 */
export const matchPlay: Format = {
  id: "matchPlay",
  name: "Match Play",
  scope: "team",
  uses: { teamPoints: true, handicapAllowance: true },
  entry: "perPlayer",
  defaults: { handicapAllowance: 100 },
  configSchema: {
    type: "object",
    properties: {
      sides: {
        type: "string",
        enum: ["singles", "fourBall"],
        default: "singles",
        title: "Match type",
        description:
          "Singles is one player a side; four-ball pairs players up, each side counting its better net score. Only affects the pairings the generator builds and the allowance — scoring follows whatever sides you set.",
      },
    },
  },
  perHoleSchema: null,

  score({ round, course, players, scores, onlyHoles, settled }): RoundResult {
    const holeNumbers = holeNumbersInPlay(round, course);
    const played = onlyHoles ? new Set(onlyHoles) : null;
    const walks = walkMatches(round, course, players, scores, onlyHoles, settled);

    const points: Record<string, number> = {};
    for (const p of players) points[p.id] = 0;
    const segmentResults: NonNullable<RoundResult["segments"]> = [];

    for (const [segmentIndex, segment] of round.segments.entries()) {
      const allHoles = holesInSegment(holeNumbers, segment.startHole, segment.endHole);
      const segPoints: Record<string, number> = {};

      for (const walk of walks.filter((w) => w.segmentIndex === segmentIndex)) {
        if (!walk.decided) continue;

        const teamPoints = round.teamPoints ?? { win: 0, loss: 0 };
        const split = splitTeamPoints(
          { id: "0", value: walk.won[0] },
          { id: "1", value: walk.won[1] },
          teamPoints,
          { lowerIsBetter: false },
        );
        for (const id of walk.sides[0]) segPoints[id] = split.get("0") ?? 0;
        for (const id of walk.sides[1]) segPoints[id] = split.get("1") ?? 0;
      }

      for (const [id, pts] of Object.entries(segPoints)) points[id] = (points[id] ?? 0) + pts;

      segmentResults.push({
        startHole: segment.startHole,
        endHole: segment.endHole,
        points: segPoints,
        holesScored: played ? allHoles.filter((h) => played.has(h)).length : allHoles.length,
        holesTotal: allHoles.length,
      });
    }

    return { roundId: round.id, points, segments: segmentResults, provisional: Boolean(onlyHoles) };
  },

  /** Which holes this player's side won, and where each left the match. Reads the same
   * walk `score` does, so a card and the points it produced can't disagree. */
  explainHoles({ round, course, players, scores, onlyHoles, settled, playerId }) {
    const notes = new Map<number, string>();

    for (const walk of walkMatches(round, course, players, scores, onlyHoles, settled)) {
      const side = walk.sides[0].includes(playerId)
        ? 0
        : walk.sides[1].includes(playerId)
          ? 1
          : null;
      if (side == null) continue;

      for (const hole of walk.holes) {
        switch (hole.state) {
          case "gap":
            break; // nothing decided here, so there is nothing to say about it
          case "over":
            notes.set(hole.hole, "match already won");
            break;
          case "played":
            notes.set(
              hole.hole,
              hole.winner == null
                ? `halved, ${matchStatus(hole.won, side)}`
                : hole.winner === side
                  ? `won, ${matchStatus(hole.won, side)}`
                  : `lost, ${matchStatus(hole.won, side)}`,
            );
            break;
        }
      }
    }
    return notes;
  },

  /** The whole point of the format: both sides' nets on every hole, who took it, and
   * where the match stood after it. */
  explainContests({ round, course, players, scores, onlyHoles, settled }): Contest[] {
    const nameOf = (id: string) => players.find((p) => p.id === id)?.name ?? id;
    const label = (side: string[]) => side.map(nameOf).join(" & ");
    const walks = walkMatches(round, course, players, scores, onlyHoles, settled);
    const multipleSegments = round.segments.length > 1;

    return walks.map((walk, i) => {
      const labels: [string, string] = [label(walk.sides[0]), label(walk.sides[1])];
      const matchesInSegment = walks.filter((w) => w.segmentIndex === walk.segmentIndex).length;

      const title = [
        matchesInSegment > 1 ? `Match ${i + 1}` : "Match",
        multipleSegments ? `holes ${walk.startHole}-${walk.endHole}` : null,
      ]
        .filter(Boolean)
        .join(" · ");

      return {
        id: `${walk.segmentIndex}-${i}`,
        title,
        columns: [0, 1].map((s) => ({
          id: String(s),
          label: labels[s as 0 | 1],
          playerIds: walk.sides[s as 0 | 1],
        })),
        holes: walk.holes.map((hole) => ({
          hole: hole.hole,
          sides: [0, 1].map((s) => ({
            id: String(s),
            label: labels[s as 0 | 1],
            playerIds: walk.sides[s as 0 | 1],
            value: hole.values[s as 0 | 1],
            won: hole.winner === s,
          })),
          ...(hole.state === "played" ? { status: matchStatus(hole.won, 0) } : {}),
          ...(hole.state === "over"
            ? { note: "match already won" }
            : hole.state === "gap"
              ? { note: "not all scores in" }
              : hole.winner == null
                ? { note: "halved" }
                : {}),
        })),
        standing: describeStanding(walk, labels),
      };
    });
  },

  /** Sides pair off two at a time, so an odd one out has nobody to play. */
  validate(round) {
    const problems = [];
    for (const [i, segment] of round.segments.entries()) {
      const sides = segment.teams.filter((t) => t.length > 0);
      if (sides.length % 2 === 1 && sides.length > 1) {
        problems.push({
          level: "warning" as const,
          message: `Segment ${i + 1} has ${sides.length} sides. They pair off in order, so the last one has no opponent and scores nothing for these holes.`,
        });
      }
    }
    return problems;
  },

  describeRules(round, _players, course) {
    const config = (round.formatConfig ?? {}) as MatchPlayConfig;
    const sides = config.sides ?? "singles";
    const tp = round.teamPoints ?? { win: 0, loss: 0 };
    const matchCount = round.segments.reduce(
      (most, seg) => Math.max(most, pairSidesIntoMatches(seg.teams.filter((t) => t.length > 0)).length),
      0,
    );

    return [
      sides === "fourBall"
        ? `Four-ball match play across ${holeCount(round, course)} holes: each side's score on a hole is the better of its two players' net scores, and the hole is won, halved or lost on that.`
        : `Singles match play across ${holeCount(round, course)} holes: each hole is won, halved or lost by net score.`,
      matchCount > 1
        ? `Sides pair off within each segment — side 1 plays side 2, side 3 plays side 4 — so ${matchCount} matches run over the same holes, each scored on its own.`
        : "Whoever wins the most holes wins the match.",
      `Each match: win ${tp.win} points, loss ${tp.loss}, and a halved match (equal holes won) splits ${(tp.win + tp.loss) / 2} each.`,
      // Worth stating outright: it is the one place the app's handicaps work differently
      // from every other format, and players will notice the strokes moving.
      `Handicaps are played off the difference: the lowest handicap in each match plays off scratch and the others receive the difference, at ${round.handicapAllowance}% allowance${allowanceNote(round, course)}.`,
      "A match stops counting once it is mathematically won.",
    ];
  },
};

/**
 * The conventional allowance for each match type, offered by the setup screen when the
 * organiser switches between them: singles play the full difference, four-ball 90% of it.
 */
export function conventionalAllowance(sides: Sides): number {
  return sides === "fourBall" ? 90 : 100;
}
